import React from 'react';
import { LANDING_STATS } from './statsData';

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const styles = `
  /* Variables and font imports are handled by global.css */

  .test-section {
    padding: 100px 60px;
    border-top: 1px solid var(--border-soft);
    background: var(--black);
    position: relative;
    overflow: hidden;
  }
  .test-section::before {
    content: '';
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse 50% 40% at 50% 0%, rgba(245,200,0,0.04) 0%, transparent 65%);
  }

  /* ── Header ── */
  .test-header {
    display: flex; justify-content: space-between; align-items: flex-end;
    margin-bottom: 72px; gap: 32px; flex-wrap: wrap;
    position: relative; z-index: 1;
  }
  .test-eyebrow {
    font-size: 10px; letter-spacing: 4px; text-transform: uppercase;
    color: var(--accent); margin-bottom: 14px;
    display: flex; align-items: center; gap: 10px;
  }
  .test-eyebrow::before {
    content: ''; width: 24px; height: 1px;
    background: var(--accent); display: inline-block;
  }
  .test-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(42px, 5.5vw, 74px); line-height: .95; color: var(--white);
  }
  .test-heading em {
    font-family: 'Instrument Serif', serif; font-style: italic; color: var(--gold);
  }
  .test-header-right {
    display: flex; flex-direction: column; align-items: flex-end; gap: 16px;
  }
  .test-sub { max-width: 300px; font-size: 13px; line-height: 1.85; color: var(--muted-light); text-align: right; }

  /* Rating pill */
  .rating-pill {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 8px 18px; border: 1px solid var(--border); border-radius: 100px;
    background: rgba(245,200,0,0.05);
  }
  .rating-num { font-family: 'Bebas Neue', sans-serif; font-size: 22px; color: var(--white); line-height: 1; }
  .rating-stars-row { display: flex; gap: 2px; }
  .rating-stars-row svg { width: 12px; height: 12px; fill: var(--accent); }
  .rating-count { font-size: 10px; color: var(--muted); letter-spacing: 1px; margin-top: 2px; }

  /* ── Featured card ── */
  .test-featured {
    border: 1px solid var(--border-soft); border-radius: 6px;
    background: var(--card-bg); padding: 40px 44px;
    margin-bottom: 16px;
    display: grid; grid-template-columns: 1fr auto;
    gap: 40px; align-items: center;
    cursor: default; transition: border-color .3s;
    position: relative; z-index: 1; overflow: hidden;
  }
  .test-featured::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, var(--accent), rgba(245,200,0,0.2));
  }
  .test-featured:hover { border-color: rgba(245,200,0,0.2); }

  .feat-quote-icon {
    width: 44px; height: 44px; color: var(--accent); opacity: .5;
    flex-shrink: 0; margin-bottom: 24px;
  }
  .feat-quote-icon svg { width: 100%; height: 100%; }

  .feat-stars { display: flex; gap: 3px; margin-bottom: 20px; }
  .feat-stars svg { width: 15px; height: 15px; fill: var(--accent); }

  .feat-text {
    font-family: 'Instrument Serif', serif; font-style: italic;
    font-size: 20px; line-height: 1.65; color: var(--white); margin-bottom: 28px;
  }
  .feat-meta { display: flex; align-items: center; gap: 14px; }
  .feat-avatar {
    width: 48px; height: 48px; border-radius: 50%;
    background: var(--accent);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Bebas Neue', sans-serif; font-size: 18px;
    color: var(--black); flex-shrink: 0;
  }
  .feat-name { font-size: 14px; font-weight: 500; color: var(--white); }
  .feat-role { font-size: 10px; color: var(--muted); letter-spacing: 1px; text-transform: uppercase; margin-top: 3px; }

  .feat-stat {
    text-align: center; padding: 28px 36px;
    border-left: 1px solid var(--border-soft);
    flex-shrink: 0;
  }
  .feat-stat-num { font-family: 'Bebas Neue', sans-serif; font-size: 64px; color: var(--accent); line-height: 1; }
  .feat-stat-label { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--muted-light); margin-top: 6px; }

  /* ── Grid ── */
  .test-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 12px; position: relative; z-index: 1;
  }

  .test-card {
    background: var(--card-bg); border: 1px solid var(--border-soft);
    border-radius: 6px; padding: 30px 26px;
    cursor: default; transition: border-color .3s, transform .3s;
    position: relative; overflow: hidden;
    display: flex; flex-direction: column;
  }
  .test-card::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
    background: var(--accent); transform: scaleX(0); transform-origin: left;
    transition: transform .35s ease;
  }
  .test-card:hover { border-color: rgba(245,200,0,0.2); transform: translateY(-4px); }
  .test-card:hover::after { transform: scaleX(1); }

  .card-quote-icon {
    width: 26px; height: 26px; color: var(--accent); opacity: .5;
    margin-bottom: 16px; flex-shrink: 0;
  }
  .card-quote-icon svg { width: 100%; height: 100%; }

  .card-stars { display: flex; gap: 3px; margin-bottom: 16px; }
  .card-stars svg { width: 12px; height: 12px; fill: var(--accent); }

  .card-text {
    font-size: 13px; line-height: 1.85; color: var(--muted-light);
    font-style: italic; flex: 1; padding-bottom: 22px; margin: 0;
  }
  .card-author {
    display: flex; align-items: center; gap: 12px;
    border-top: 1px solid var(--border-soft); padding-top: 18px; margin-top: auto;
  }
  .card-avatar {
    width: 38px; height: 38px; border-radius: 50%;
    background: rgba(245,200,0,0.15);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Bebas Neue', sans-serif; font-size: 14px; color: var(--accent);
    flex-shrink: 0;
  }
  .card-name { font-size: 13px; font-weight: 500; color: var(--white); }
  .card-role { font-size: 10px; color: var(--muted); letter-spacing: 1px; text-transform: uppercase; margin-top: 2px; }

  /* ── Trust strip ── */
  .trust-strip {
    display: flex; align-items: center; justify-content: center;
    gap: 40px; flex-wrap: wrap;
    margin-top: 48px; padding: 20px 32px;
    border: 1px solid var(--border-soft); border-radius: 6px;
    background: rgba(255,255,255,0.02);
    position: relative; z-index: 1;
  }
  .trust-item { display: flex; align-items: center; gap: 10px; font-size: 12px; color: var(--muted-light); }
  .trust-icon {
    width: 32px; height: 32px; border-radius: 6px;
    background: rgba(245,200,0,0.08);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .trust-icon svg {
    width: 14px; height: 14px; stroke: var(--accent); fill: none;
    stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round;
  }
  .trust-divider { width: 1px; height: 28px; background: var(--border-soft); }
  .trust-item strong { color: var(--white); font-weight: 500; }

  /* ── Responsive ── */
  @media (max-width: 960px) {
    .test-grid           { grid-template-columns: repeat(2, 1fr); }
    .test-featured       { grid-template-columns: 1fr; }
    .feat-stat           { border-left: none; border-top: 1px solid var(--border-soft); padding: 20px 0 0; }
    .test-section        { padding: 72px 28px; }
    .test-header-right   { align-items: flex-start; }
    .test-sub            { text-align: left; }
    .trust-strip         { gap: 20px; }
    .trust-divider       { display: none; }
  }
  @media (max-width: 640px) {
    .test-section  { padding: 64px 16px; }
    .test-grid     { grid-template-columns: 1fr; }
    .test-header   { flex-direction: column; align-items: flex-start; margin-bottom: 48px; }
    .feat-text     { font-size: 17px; }
    .test-featured { padding: 28px 24px; }
  }
`;

/* ─────────────────────────────────────────────
   REUSABLE SVGs
───────────────────────────────────────────── */
const QuoteIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.192 15.757c0-.88-.23-1.618-.69-2.217-.326-.412-.768-.683-1.327-.812-.55-.128-1.07-.137-1.54-.028-.16-.95.1-1.956.76-3.022.66-1.065 1.515-1.867 2.558-2.403L9.373 5c-.8.396-1.56.898-2.26 1.505-.71.607-1.34 1.305-1.9 2.094s-.98 1.68-1.25 2.69-.346 2.04-.217 3.1c.168 1.4.62 2.52 1.356 3.35.735.84 1.652 1.26 2.748 1.26.965 0 1.766-.29 2.4-.878.628-.576.94-1.365.94-2.368zm9.124 0c0-.88-.23-1.618-.69-2.217-.326-.42-.77-.692-1.327-.817-.56-.124-1.074-.13-1.54-.022-.16-.94.09-1.95.75-3.016.66-1.066 1.515-1.867 2.558-2.403L18.49 5c-.8.396-1.555.898-2.26 1.505-.708.607-1.34 1.305-1.894 2.094-.556.79-.97 1.68-1.24 2.69-.273 1-.345 2.04-.217 3.1.168 1.4.62 2.52 1.356 3.35.735.84 1.652 1.26 2.748 1.26.965 0 1.766-.29 2.4-.878.628-.576.94-1.365.94-2.368z" />
  </svg>
);

const StarPath = () => (
  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
);

const Stars = ({ size = 12 }) => (
  <div style={{ display: 'flex', gap: 3 }}>
    {[...Array(5)].map((_, i) => (
      <svg key={i} viewBox="0 0 24 24" style={{ width: size, height: size, fill: 'var(--accent)' }}>
        <StarPath />
      </svg>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const FEATURED = {
  text: 'WebDigiting completely transformed our online presence. Within 3 months their SEO work doubled our organic traffic and tripled our qualified leads. The team is responsive, transparent, and genuinely invested in results.',
  name: 'Client Review',
  role: 'SEO & Growth Strategy',
  initials: 'CR',
  stat: { num: '3×', label: 'Lead Growth\nin 90 Days' },
};

const TESTIMONIALS = [
  {
    text: 'The app they built was delivered on time and exceeded every expectation. The UI is beautiful, performance flawless, and our users absolutely love it.',
    name: 'Client Review',
    role: 'Product Design & Development',
    initials: 'CR',
  },
  {
    text: 'Their branding work gave us a completely fresh identity. The team understood our vision immediately and delivered a brand system that perfectly captures who we are.',
    name: 'Client Review',
    role: 'Brand Strategy & Design',
    initials: 'CR',
  },
  {
    text: 'Our social media grew from 800 to 12,000 followers in 4 months. The content calendar and community management has been game-changing for our brand awareness.',
    name: 'Client Review',
    role: 'Social Media Management',
    initials: 'CR',
  },
];

const TRUST_ITEMS = [
  { icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', label: 'Satisfaction Guarantee', value: LANDING_STATS.satisfactionGuarantee + '%' },
  { icon: 'M12 12m-10 0a10 10 0 1 0 20 0 10 10 0 0 0-20 0M12 6v6l4 2', label: 'Response Time', value: '24h' },
  { icon: 'M22 12h-4l-3 9L9 3l-3 9H2', label: 'Projects Delivered', value: LANDING_STATS.projectsCompleted + '+' },
  { icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8', label: 'Happy Clients', value: LANDING_STATS.happyClients + '+' },
];

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const LandingTestimonials = () => (
  <>
    <style>{styles}</style>

    <section className="test-section" id="testimonials">

      {/* ── Header ── */}
      <div className="test-header">
        <div>
          <p className="test-eyebrow">Client Reviews</p>
          <h2 className="test-heading">What They <em>Say</em></h2>
        </div>
        <div className="test-header-right">
          <div className="rating-pill">
            <span className="rating-num">4.9</span>
            <div>
              <div className="rating-stars-row">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} viewBox="0 0 24 24"><StarPath /></svg>
                ))}
              </div>
              <p className="rating-count">Based on 40+ reviews</p>
            </div>
          </div>
          <p className="test-sub">
            Don't take our word for it — hear from the brands we've helped scale.
          </p>
        </div>
      </div>

      {/* ── Featured card ── */}
      <div className="test-featured">
        <div>
          <div className="feat-quote-icon"><QuoteIcon /></div>
          <div className="feat-stars"><Stars size={15} /></div>
          <p className="feat-text">"{FEATURED.text}"</p>
          <div className="feat-meta">
            <div className="feat-avatar">{FEATURED.initials}</div>
            <div>
              <p className="feat-name">{FEATURED.name}</p>
              <p className="feat-role">{FEATURED.role}</p>
            </div>
          </div>
        </div>
        <div className="feat-stat">
          <div className="feat-stat-num">{FEATURED.stat.num}</div>
          <div className="feat-stat-label"
            dangerouslySetInnerHTML={{ __html: FEATURED.stat.label.replace('\n', '<br/>') }}
          />
        </div>
      </div>

      {/* ── Cards grid ── */}
      <div className="test-grid">
        {TESTIMONIALS.map((t) => (
          <div key={t.name} className="test-card">
            <div className="card-quote-icon"><QuoteIcon /></div>
            <div className="card-stars"><Stars size={12} /></div>
            <p className="card-text">"{t.text}"</p>
            <div className="card-author">
              <div className="card-avatar">{t.initials}</div>
              <div>
                <p className="card-name">{t.name}</p>
                <p className="card-role">{t.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Trust strip ── */}
      <div className="trust-strip">
        {TRUST_ITEMS.map((item, i) => (
          <React.Fragment key={item.label}>
            <div className="trust-item">
              <div className="trust-icon">
                <svg viewBox="0 0 24 24">
                  <path d={item.icon} />
                </svg>
              </div>
              <span><strong>{item.value}</strong> {item.label}</span>
            </div>
            {i < TRUST_ITEMS.length - 1 && <div className="trust-divider" />}
          </React.Fragment>
        ))}
      </div>

    </section>
  </>
);

export default LandingTestimonials;